import React, { useCallback } from "react";
import { darkTheme } from "@yleisradio/yds-core";
import { Play } from "@yleisradio/yds-icons-react";
import styled from "styled-components";

import { getRecommendationUrl } from "../../helpers/areenaLinks";
import { truncate } from "../../helpers/commonUtils";
import { playRecommendation } from "../../helpers/recommendations";
import { Recommendation } from "../../service/areena-recommendations";
import i18n from "../../service/i18n";
import { usePlayerStore } from "../../store/usePlayerStore";
import { linkStyle } from "../../styles/global";
import { focusColor, recommendationTextColor } from "../../styles/variables";
import { PlayerUpdateEvent } from "../../types/public";
import {
  Background,
  openSansBold,
  RecommendationItemBackground,
  smallHeaderFont,
  TextShadow
} from "../recommendations/RecommendationStyles";
import {
  SetPlayingRecommendation,
  UiSize
} from "../recommendations/recommendationTypes";

const RecommendationItem = styled.div`
  flex: 1;
  max-width: 30%;
  min-width: 210px;
  position: relative;
  margin-right: 20px;

  &:last-child {
    margin-right: 0;
  }
`;

const ThumbnailLink = styled.a`
  &&& {
    ${linkStyle}
  }

  &:focus {
    outline: none;
  }

  &:focus-visible:before {
    content: "";
    display: block;
    height: calc(100% - 2px);
    left: 1px;
    outline: 1px solid ${focusColor};
    pointer-events: none;
    position: absolute;
    top: 1px;
    width: calc(100% - 2px);
    z-index: 1;
  }
`;

const RecommendationGradient = styled(Background)`
  &:hover {
    filter: opacity(0.6);
  }
  transition: filter 0.2s ease;
  background: linear-gradient(to bottom, transparent, black);
  background-clip: content-box;
  box-shadow: 0 2px 4px 0 rgb(0 0 0 / 100%);
`;

const RecommendationText = styled(TextShadow)`
  bottom: 0;
  color: ${recommendationTextColor};
  padding: 1em;
  pointer-events: none;
  position: absolute;
`;

const SmallUiRecommendationHeader = styled.span`
  &&& {
    display: block;
    height: 2.4em;
    line-height: 1.2em;
    margin: 1em 0 0.6em;
    overflow: hidden;
    ${openSansBold}
    ${smallHeaderFont}
  }
`;

const LargeUiRecommendationHeader = styled.span`
  display: block;
  font-size: 1.125em;
  height: 2.4em;
  line-height: 1.2em;
  margin: 1em 0 0.3em;
  overflow: hidden;
  ${openSansBold}
`;

const RecommendationDescription = styled.div`
  font-size: 0.938em;
`;

const RecommendationPlayButton = styled.button`
  align-items: center;
  background: transparent;
  border: 2px solid ${darkTheme.BORDER};
  border-radius: 100%;
  color: ${darkTheme.ACTION_PRIMARY};
  cursor: pointer;
  display: flex;
  height: 44px;
  justify-content: center;
  position: absolute;
  right: 0.5rem;
  top: 0.5rem;
  width: 44px;
  transition: 0.2s opacity;

  &:hover {
    opacity: 0.8;
  }

  &:focus-visible {
    outline: 1px solid ${focusColor} !important;
    outline-offset: 2px;
  }
`;

export const RecommendationCard = ({
  children,
  disabled,
  index,
  language,
  recommendation,
  setPlayingRecommendation,
  showDescription = false,
  uiSize,
  update
}: {
  children?: React.ReactNode;
  disabled?: boolean;
  index: number;
  language: string;
  recommendation: Recommendation;
  setPlayingRecommendation: SetPlayingRecommendation;
  showDescription?: boolean;
  uiSize: UiSize;
  update: PlayerUpdateEvent;
}) => {
  const { getState } = usePlayerStore();
  const url = getRecommendationUrl({
    index,
    language,
    programId: recommendation.id
  });

  const onClick = useCallback(
    (playProgram: boolean) => e => {
      e.preventDefault();
      e.stopPropagation();

      const programId = getState().programId;

      if (disabled || !programId) {
        return;
      }
      playRecommendation({
        index,
        language,
        playProgram,
        programId,
        recommendation,
        setPlayingRecommendation,
        update
      });
    },
    [
      disabled,
      getState,
      index,
      language,
      recommendation,
      setPlayingRecommendation,
      update
    ]
  );

  const playButtonAriaLabel = disabled
    ? ""
    : `${i18n("toista", { language })} ${recommendation.title}`;

  return (
    <RecommendationItem>
      <ThumbnailLink
        href={disabled ? "#" : url}
        onClick={onClick(false)}
        tabIndex={disabled ? -1 : 0}>
        <RecommendationItemBackground image={recommendation.image} />
        <RecommendationGradient />
        <RecommendationText>
          {uiSize === UiSize.Large ? (
            <>
              <LargeUiRecommendationHeader>
                {recommendation.title}
              </LargeUiRecommendationHeader>
              {showDescription && (
                <RecommendationDescription>
                  {truncate(recommendation.description, 74)}
                </RecommendationDescription>
              )}
            </>
          ) : (
            <SmallUiRecommendationHeader>
              {recommendation.title}
            </SmallUiRecommendationHeader>
          )}
        </RecommendationText>
      </ThumbnailLink>
      <RecommendationPlayButton
        aria-label={playButtonAriaLabel}
        onClick={onClick(true)}>
        <Play />
      </RecommendationPlayButton>
      {children}
    </RecommendationItem>
  );
};
